import request from '@/config/axios'
import { TransferReqVO } from '@/api/crm/permission'

export interface CustomerVO {
  id: number // 编号
  name: string // 客户名称
  contactName: string // 主要联系人
  followUpStatus: boolean // 跟进状态
  contactLastTime: Date // 最后跟进时间
  contactLastContent: string // 最后跟进内容
  contactNextTime: Date // 下次联系时间
  ownerUserId: number // 负责人的用户编号
  ownerUserName?: string // 负责人的用户名称
  ownerUserDept?: string // 负责人的部门名称
  ownerUserDeptName?: string // 负责人的部门名称
  lockStatus?: boolean
  dealStatus?: boolean
  mobile: string // 手机号
  telephone: string // 电话
  qq: string // QQ
  wechat: string // wechat
  email: string // email
  otherInfo: string // 其他
  areaId: number // 所在地
  areaName?: string // 所在地名称
  detailAddress: string // 详细地址
  industryId: number // 所属行业
  level: number // 客户等级
  source: number // 客户来源
  sourceOther: string // 客户来源选择「其他」时的补充说明
  visitLeadInfo: string // 来访线索信息
  requirementCategoryId: number // 需求分类
  requirementSummary: string // 需求概述
  requirementDetail: string // 需求详细说明
  customerCategory: number // 客户种类
  customerStatus: number // 客户状态
  customerType: number // 客户类型
  groupCreated: boolean // 是否建群
  budget: number // 预算
  quotation: number // 报价
  legalPerson: string // 法人代表
  registeredCapital: string // 注册资本
  establishDate: Date // 成立日期
  businessScope: string // 经营范围
  ownerPosition: string // 负责人职务
  ownerRemark: string // 负责人备注
  socialSecurityStaff: string // 社保人员
  listed: boolean // 是否上市
  projectApproved: number // 项目是否立项
  expectedOnlineTime: Date // 预计上线时间
  website: string // 官网
  trafficRoute: string // 交通路线
  partyAFiles: string // 甲方相关文件
  partyBFiles: string // 乙方相关文件
  quotationRemark: string // 报价备注
  quotationFiles: string // 报价附件
  remark: string // 备注
  creator: string // 创建人
  creatorName?: string // 创建人名称
  creatorDeptName?: string // 创建人所在部门（申请部门）
  createTime: Date // 创建时间
  updateTime: Date // 更新时间
  poolDay?: number // 距离进入公海天数
  starred?: boolean // 是否标星（重点客户），列表公司名称后显示 ★
  topped?: boolean // 是否置顶，列表公司名称后显示 ↑、排序置顶优先
  shared?: boolean // 是否已共享给其他用户，列表公司名称后显示 ☇
}

// 客户共享记录
export interface CustomerShareVO {
  id: number // 编号
  customerId: number // 客户编号
  fromUserId: number // 共享人的用户编号
  fromUserName?: string // 共享人昵称
  toUserId: number // 被共享人的用户编号
  toUserName?: string // 被共享人昵称
  shareTime: Date // 共享时间
}

// 查询客户列表
export const getCustomerPage = async (params) => {
  return await request.get({ url: `/crm/customer/page`, params })
}

// 进入公海客户提醒的客户列表
export const getPutPoolRemindCustomerPage = async (params) => {
  return await request.get({ url: `/crm/customer/put-pool-remind-page`, params })
}

// 获得待进入公海客户数量
export const getPutPoolRemindCustomerCount = async () => {
  return await request.get({ url: `/crm/customer/put-pool-remind-count` })
}

// 获得今日需联系客户数量
export const getTodayContactCustomerCount = async () => {
  return await request.get({ url: `/crm/customer/today-contact-count` })
}

// 获得分配给我、待跟进的线索数量的客户数量
export const getFollowCustomerCount = async () => {
  return await request.get({ url: `/crm/customer/follow-count` })
}

// 查询客户详情
export const getCustomer = async (id: number) => {
  return await request.get({ url: `/crm/customer/get?id=` + id })
}

// 新增客户
export const createCustomer = async (data: CustomerVO) => {
  return await request.post({ url: `/crm/customer/create`, data })
}

// 修改客户
export const updateCustomer = async (data: CustomerVO) => {
  return await request.put({ url: `/crm/customer/update`, data })
}

// 更新客户的成交状态
export const updateCustomerDealStatus = async (id: number, dealStatus: boolean) => {
  return await request.put({ url: `/crm/customer/update-deal-status`, params: { id, dealStatus } })
}

// 删除客户
export const deleteCustomer = async (id: number) => {
  return await request.delete({ url: `/crm/customer/delete?id=` + id })
}

// 导出客户 Excel
export const exportCustomer = async (params: any) => {
  return await request.download({ url: `/crm/customer/export-excel`, params })
}

// 下载客户导入模板
export const importCustomerTemplate = () => {
  return request.download({ url: '/crm/customer/get-import-template' })
}

// 导入客户
export const handleImport = async (formData) => {
  return await request.upload({ url: `/crm/customer/import`, data: formData })
}

// 客户列表
export const getCustomerSimpleList = async () => {
  return await request.get({ url: `/crm/customer/simple-list` })
}

// ======================= 业务操作 =======================

// 客户转移
export const transferCustomer = async (data: TransferReqVO) => {
  return await request.put({ url: '/crm/customer/transfer', data })
}

// 锁定/解锁客户
export const lockCustomer = async (id: number, lockStatus: boolean) => {
  return await request.put({ url: `/crm/customer/lock`, data: { id, lockStatus } })
}

// 批量标星 / 取消标星（重点客户）
export const updateCustomerStarred = async (ids: number[], starred: boolean) => {
  return await request.put({ url: `/crm/customer/update-starred`, data: { ids, starred } })
}

// 批量置顶 / 取消置顶
export const updateCustomerTopped = async (ids: number[], topped: boolean) => {
  return await request.put({ url: `/crm/customer/update-topped`, data: { ids, topped } })
}

// 领取公海客户
export const receiveCustomer = async (ids: any[]) => {
  return await request.put({ url: '/crm/customer/receive', params: { ids: ids.join(',') } })
}

// 分配公海给对应负责人
export const distributeCustomer = async (ids: any[], ownerUserId: number) => {
  return await request.put({
    url: '/crm/customer/distribute',
    data: { ids: ids, ownerUserId }
  })
}

// 客户放入公海
export const putCustomerPool = async (id: number) => {
  return await request.put({ url: `/crm/customer/put-pool?id=${id}` })
}

// 批量将客户放入公海（我的客户 → 公海客户）
export const putCustomerPoolBatch = async (ids: number[]) => {
  return await request.put({ url: '/crm/customer/put-pool-batch', params: { ids: ids.join(',') } })
}

// ======================= 客户共享 =======================

// 批量共享客户给其他用户
export const shareCustomer = async (data: { customerIds: number[]; toUserIds: number[] }) => {
  return await request.put({ url: '/crm/customer/share', data })
}

// 批量取消共享客户
export const unshareCustomer = async (data: { customerIds: number[]; toUserIds: number[] }) => {
  return await request.put({ url: '/crm/customer/unshare', data })
}

// 获得客户的共享记录列表
export const getCustomerShareList = async (customerIds: number[]) => {
  return await request.get({
    url: '/crm/customer/share-list',
    params: { customerIds: customerIds.join(',') }
  })
}
